import React from 'react';
import {
  RiskDot,
  Stat,
  conditionFromZone,
  trendGlyph,
  fiscalYearOf,
  cadenceFromZone
} from './primitives';
import { primaryAction, useCaseLabel } from './decisionMapping';

/**
 * Layer 1 — Position headline + Condition metadata (P1.1).
 * Position is the largest type; Condition is supporting metadata.
 *
 * Declined Opinions use a short band; all hook-shaped helpers still run
 * unconditionally so render order never skips a slot.
 */
const VerdictBand = ({ company, useCase, billing }) => {
  const { company: companyInfo, analysis } = company;
  const { distressScore, trend, aiAnalysis, dataQuality, dataLineage } = analysis;

  // Always call before any early return — ESLint treats useCaseLabel as a Hook
  // because of the `use*` name (it is a plain label helper, not React state).
  const lensLabel = useCaseLabel(useCase);
  const isDeclined = Boolean(aiAnalysis?.declined || aiAnalysis?.outcome === 'declined');

  const condition = aiAnalysis.condition
    ? {
        label: aiAnalysis.condition,
        tone:
          aiAnalysis.condition === 'Sound'
            ? 'low'
            : aiAnalysis.condition === 'Distressed'
              ? 'high'
              : 'medium'
      }
    : conditionFromZone(
        distressScore?.riskZone,
        aiAnalysis?.committeeDecision?.overallFinancialHealth
      );

  const trendInfo = trend && trend.available ? trendGlyph(trend.overall) : null;
  const isPrivate = companyInfo.ownership === 'private';
  const action = primaryAction(distressScore?.riskZone, useCase, aiAnalysis);

  const position =
    aiAnalysis.position ||
    `${action.text}. ${action.reasoning || aiAnalysis.executiveSummary || ''}`.trim();

  const binding =
    aiAnalysis.bindingConstraint ||
    (aiAnalysis.concerns && aiAnalysis.concerns[0]) ||
    '—';

  const fiscalYear = fiscalYearOf(dataLineage?.financialPeriodEnd);
  const roleLabel =
    aiAnalysis?.counterpartyRole === 'customer'
      ? 'Customer (you sell to them)'
      : aiAnalysis?.counterpartyRole === 'supplier'
        ? 'Supplier (you buy from them)'
        : null;
  const basis = [
    fiscalYear ? `FY${String(fiscalYear).slice(-2)} statements` : null,
    dataLineage?.filingForm || (isPrivate ? 'user-supplied financials' : null),
    lensLabel,
    roleLabel
  ]
    .filter(Boolean)
    .join(' · ');

  const figureRows = Array.isArray(aiAnalysis.figureRows) ? aiAnalysis.figureRows : [];
  const opinionRows = Array.isArray(aiAnalysis.opinionRows) ? aiAnalysis.opinionRows : [];
  const bindsOnFigure = figureRows.some((row) => row.binds);
  const opinionNo = company.opinionNumber || aiAnalysis.opinionNumber || dataLineage?.opinionNumber;
  const opinionDate = company.opinionDate || aiAnalysis.opinionDate || dataLineage?.opinionDate;

  if (isDeclined) {
    return (
      <div className="fr-verdict">
        <div className="fr-opinion-docmeta">
          <span className="fr-label">Counterparty Opinion</span>
          <span className="fr-opinion-docmeta-id">Declined</span>
        </div>
        <h3 className="fr-verdict-name">{companyInfo.name}</h3>
        <p className="fr-summary-text">
          {aiAnalysis.executiveSummary ||
            `Novrick declines to opine on ${companyInfo.name}: the record does not support three defensible evidence lines.`}
        </p>
        <p className="fr-summary-text">
          {aiAnalysis.declinedReason}
          {aiAnalysis.customerCanSupply
            ? ` You can supply: ${aiAnalysis.customerCanSupply}.`
            : ''}
        </p>
        <p className="fr-summary-text">
          You were not charged
          {billing?.reason === 'declined_not_charged' || aiAnalysis.notCharged
            ? ', and this does not consume a Desk allowance'
            : ''}
          .
        </p>
      </div>
    );
  }

  return (
    <div className="fr-verdict">
      <div className="fr-opinion-docmeta">
        <span className="fr-label">Counterparty Opinion</span>
        <span className="fr-opinion-docmeta-id fr-num">
          {opinionNo != null ? `No. ${opinionNo}` : null}
          {opinionNo != null && opinionDate ? ' · ' : null}
          {opinionDate || null}
        </span>
      </div>

      <div className="fr-verdict-topline">
        <div className="fr-verdict-identity">
          <span className="fr-label">Counterparty</span>
          <h3 className="fr-verdict-name">{companyInfo.name}</h3>
          <span className="fr-verdict-meta">
            {[
              isPrivate ? 'Private' : companyInfo.ticker,
              companyInfo.industry,
              companyInfo.concentrationLabel || null
            ]
              .filter(Boolean)
              .join(' · ')}
            {basis ? ` · ${basis}` : ''}
          </span>
        </div>
      </div>

      {opinionRows.length > 0 ? (
        <OpinionMemo rows={opinionRows} />
      ) : (
        <div className="fr-position">
          <span className="fr-label">Position</span>
          <p className="fr-position-text">{position}</p>
        </div>
      )}

      {figureRows.length > 0 ? (
        <StatementMemo rows={figureRows} />
      ) : (
        <div className="fr-condition-verdict">
          <span className="fr-label">Condition</span>
          <p className="fr-condition-verdict-text">
            {aiAnalysis.conditionVerdict ||
              `${condition.label}. ${binding !== '—' ? binding : ''}`.trim()}
          </p>
        </div>
      )}

      <div className={`fr-verdict-grid fr-verdict-grid-meta${bindsOnFigure ? ' fr-verdict-grid-four' : ''}`}>
        <Stat label="Band">
          <div className={`fr-stat-value fr-text-${condition.tone}`}>
            <RiskDot tone={condition.tone} />
            {condition.label}
          </div>
        </Stat>

        <Stat
          label="Year-to-year"
          sub={
            trend && trend.available
              ? `${fiscalYearOf(trend.firstPeriod)}–${fiscalYearOf(trend.lastPeriod)}, own history`
              : 'Single period'
          }
        >
          <div className="fr-stat-value">
            {trendInfo ? (
              <>
                <span className={`fr-text-${trendInfo.tone}`} aria-hidden="true">
                  {trendInfo.glyph}
                </span>
                {trend.overall}
              </>
            ) : (
              'N/A'
            )}
          </div>
        </Stat>

        <Stat label="Confidence" sub={dataQuality ? `Data completeness: ${dataQuality.score}%` : null}>
          <div className="fr-stat-value">{dataQuality ? dataQuality.level : 'N/A'}</div>
        </Stat>

        <Stat label="Monitoring" sub="Suggested cadence">
          <div className="fr-stat-value">{cadenceFromZone(distressScore.riskZone, aiAnalysis)}</div>
        </Stat>

        {bindsOnFigure ? null : (
          <Stat label="Binding Constraint">
            <div className="fr-stat-sentence">{binding}</div>
          </Stat>
        )}
      </div>
    </div>
  );
};

function figureNote(row) {
  if (row.note) return row.note;
  if (row.binds) return 'Binds';
  return null;
}

function StatementMemo({ rows }) {
  return (
    <div className="fr-figures">
      {rows.map((row) => {
        const note = figureNote(row);
        return (
          <div key={row.label} className="fr-figure">
            <span className="fr-label">{row.label}</span>
            <span className="fr-num">{row.value}</span>
            {note ? <span className="fr-figure-note">{note}</span> : null}
          </div>
        );
      })}
    </div>
  );
}

function OpinionMemo({ rows }) {
  return (
    <div className="fr-opinion">
      {rows.map((row) => (
        <p key={row.text || row.label}>{row.text || `${row.label} ${row.value}`}</p>
      ))}
    </div>
  );
}

export default VerdictBand;
